import { CdkDragEnd, DragDropModule } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnInit,
  Output,
  Signal,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

interface IDragPosition {
  x: number;
  y: number;
}

/** Where the floating keypad on a board remembers its position. */
export const KEYPAD_POSITION_STORAGE_KEY = 'screen-lock-keypad-position';
const MAX_PIN_LENGTH = 12;
/** Mirrors the API's `^\d{4,12}$`: submitting anything shorter can only ever come back as a 400. */
const MIN_PIN_LENGTH = 4;
const VIEWPORT_MARGIN_PX = 16;

/**
 * Numeric keypad for the screen lock. Built from `<button>` elements only — NO `<input>`, ever: a real input
 * would pop the device soft keyboard on factory-floor touch displays. See SCREEN_LOCK_CONTEXT.md.
 */
@Component({
  imports: [DragDropModule, MatButtonModule, MatIconModule, TranslateModule],
  selector: 'app-virtual-number-keyboard',
  standalone: true,
  styleUrl: './virtual-number-keyboard.component.scss',
  templateUrl: './virtual-number-keyboard.component.html',
})
export class VirtualNumberKeyboardComponent implements OnInit, AfterViewInit {
  /** Which screen is being unlocked. Empty falls back to a generic "Enter PIN". */
  public title = input<string>('');
  /** Why the PIN is being asked for; hidden when empty. */
  public subtitle = input<string>('');
  public hasError = input<boolean>(false);
  /** True while the API is checking the PIN; blocks a second submit and greys the keypad. */
  public isVerifying = input<boolean>(false);
  /** Selector of the ancestor the keypad may not be dragged out of. */
  public boundarySelector = input<string>('body');
  /**
   * localStorage key for the dragged position. Each host needs its own — the stored value is an offset, so a
   * board keypad's offset replayed into a centred dialog would throw it off screen. Ignored while
   * `openAtPoint` is set: a tap-positioned keypad is transient and never persisted.
   */
  public positionStorageKey = input<string>(KEYPAD_POSITION_STORAGE_KEY);
  /**
   * Viewport point (`clientX`/`clientY`) to open the keypad at — the operator's last tap on the board. When
   * set, the keypad opens centred on it (clamped to the viewport) and its position is NOT remembered: each
   * open follows the tap instead of a saved offset. Left null by the settings dialog, which keeps the
   * position it was last dragged to.
   */
  public openAtPoint = input<IDragPosition | null>(null);
  /** Shows the "Go to Home" escape hatch for an operator stranded on a locked board. Off in the settings dialog (it has its own cancel). */
  public showHomeButton = input<boolean>(false);

  @Output() public submitted = new EventEmitter<string>();
  @Output() public closed = new EventEmitter<void>();
  @Output() public goHome = new EventEmitter<void>();

  @ViewChild('keypad') private keypad?: ElementRef<HTMLElement>;

  public readonly digitKeys: readonly string[] = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
  public readonly dragPosition = signal<IDragPosition>({ x: 0, y: 0 });

  private readonly translate = inject(TranslateService);

  private readonly pin = signal<string>('');

  public readonly headerTitle: Signal<string> = computed(
    () => this.title() || this.translate.instant('general.screenLock.enterPin'),
  );

  public readonly isEmpty: Signal<boolean> = computed(() => this.pin().length === 0);
  /** The unlock key stays disabled until the PIN could actually be one. */
  public readonly isSubmittable: Signal<boolean> = computed(() => this.pin().length >= MIN_PIN_LENGTH);
  public readonly pinDots: Signal<readonly number[]> = computed(() =>
    Array.from({ length: this.pin().length }, (_: unknown, index: number) => index),
  );

  constructor() {
    effect(() => {
      // A rejected PIN must never stay on screen — clear it as soon as the parent reports the failure.
      if (this.hasError()) {
        this.pin.set('');
      }
    });
  }

  public ngOnInit(): void {
    // A tap-positioned keypad ignores any saved offset — it opens where the operator last tapped instead.
    if (!this.openAtPoint()) {
      this.loadPosition();
    }
  }

  public ngAfterViewInit(): void {
    // Deferred so the write to `dragPosition` lands after the current CD pass (it feeds cdkDragFreeDragPosition).
    setTimeout(() => {
      const point: IDragPosition | null = this.openAtPoint();

      if (point) {
        this.positionAtPoint(point);
      } else {
        this.keepInsideViewport();
      }
    });
  }

  /** Desktop convenience; opens no soft keyboard (there is no focusable text field to attach one to). */
  @HostListener('document:keydown', ['$event'])
  public onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key.length === 1 && event.key >= '0' && event.key <= '9') {
      this.onDigit(event.key);
    } else if (event.key === 'Backspace') {
      this.onBackspace();
    } else if (event.key === 'Enter') {
      this.onSubmit();
    } else if (event.key === 'Escape') {
      this.onClose();
    } else {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  }

  public onDigit(digit: string): void {
    if (this.pin().length >= MAX_PIN_LENGTH) {
      return;
    }

    this.pin.update((current: string) => current + digit);
  }

  public onBackspace(): void {
    this.pin.update((current: string) => current.slice(0, -1));
  }

  public onSubmit(): void {
    if (!this.isSubmittable() || this.isVerifying()) {
      return;
    }

    this.submitted.emit(this.pin());
  }

  public onClose(): void {
    this.closed.emit();
  }

  public onGoHome(): void {
    this.goHome.emit();
  }

  public onDragEnded(event: CdkDragEnd): void {
    this.dragPosition.set(event.source.getFreeDragPosition());

    // A tap-positioned keypad is transient: keep the drag for this open, but never write it back.
    if (!this.openAtPoint()) {
      this.savePosition();
    }
  }

  private loadPosition(): void {
    try {
      const saved: string | null = localStorage.getItem(this.positionStorageKey());

      if (!saved) {
        return;
      }

      const parsed: unknown = JSON.parse(saved);

      if (this.isDragPosition(parsed)) {
        this.dragPosition.set({ x: parsed.x, y: parsed.y });
      }
    } catch {
      this.dragPosition.set({ x: 0, y: 0 });
    }
  }

  private savePosition(): void {
    localStorage.setItem(this.positionStorageKey(), JSON.stringify(this.dragPosition()));
  }

  /**
   * Opens the keypad with its top-left corner at the operator's last tap (the keypad drops down and to the
   * right of the tap), clamped to stay fully inside the viewport. Runs with `dragPosition` still at
   * `{ 0, 0 }`, so the measured rect is the keypad's natural (centred) box and the result is the offset from
   * there onto the tap. Not persisted — see `openAtPoint`.
   */
  private positionAtPoint(point: IDragPosition): void {
    if (!this.keypad) {
      return;
    }

    const rect: DOMRect = this.keypad.nativeElement.getBoundingClientRect();
    const maxX: number = window.innerWidth - rect.width - VIEWPORT_MARGIN_PX;
    const maxY: number = window.innerHeight - rect.height - VIEWPORT_MARGIN_PX;
    const left: number = this.clampToViewport(point.x, maxX);
    const top: number = this.clampToViewport(point.y, maxY);

    this.dragPosition.set({ x: left - rect.left, y: top - rect.top });
  }

  /** Keeps a coordinate within `[VIEWPORT_MARGIN_PX, max]`, tolerating a `max` below the margin on tiny screens. */
  private clampToViewport(value: number, max: number): number {
    return Math.min(Math.max(value, VIEWPORT_MARGIN_PX), Math.max(VIEWPORT_MARGIN_PX, max));
  }

  /** Screens differ between plants — a position saved on a large display must not push the keypad off a small one. */
  private keepInsideViewport(): void {
    if (!this.keypad) {
      return;
    }

    const rect: DOMRect = this.keypad.nativeElement.getBoundingClientRect();
    const current: IDragPosition = this.dragPosition();
    let { x, y } = current;

    if (rect.right > window.innerWidth) {
      x -= rect.right - window.innerWidth + VIEWPORT_MARGIN_PX;
    }

    if (rect.bottom > window.innerHeight) {
      y -= rect.bottom - window.innerHeight + VIEWPORT_MARGIN_PX;
    }

    if (rect.left < 0) {
      x += -rect.left + VIEWPORT_MARGIN_PX;
    }

    if (rect.top < 0) {
      y += -rect.top + VIEWPORT_MARGIN_PX;
    }

    if (x !== current.x || y !== current.y) {
      this.dragPosition.set({ x, y });
      this.savePosition();
    }
  }

  private isDragPosition(value: unknown): value is IDragPosition {
    const candidate = value as IDragPosition | null;

    return (
      typeof candidate === 'object' &&
      candidate !== null &&
      Number.isFinite(candidate.x) &&
      Number.isFinite(candidate.y)
    );
  }
}
